import { useMemo } from 'react';

// Domain mapping: add more info as needed (currency, language, etc.)
const DOMAIN_REGION_MAP = [
  {
    domains: ['matwproject.org', 'localhost'],
    region: 'AU',
    country: 'Australia',
    language: 'en',
    currency: 'AUD',
    isDefault: true,
  },
  {
    domains: ['matwproject.org.uk'],
    region: 'UK',
    country: 'United Kingdom',
    language: 'en',
    currency: 'GBP',
  },
  {
    domains: ['matwprojectusa.org'],
    region: 'US',
    country: 'United States',
    language: 'en',
    currency: 'USD',
  },
  {
    domains: ['matwprojectca.org'],
    region: 'CA',
    country: 'Canada',
    language: 'en',
    currency: 'CAD',
  },
  {
    domains: ['matwprojectid.org'],
    region: 'ID',
    country: 'Indonesia',
    language: 'id',
    currency: 'IDR',
  },
  {
    domains: ['matwprojectie.org'],
    region: 'IE',
    country: 'Ireland',
    language: 'en',
    currency: 'EUR',
  },
  {
    domains: ['matwprojectme.org'],
    region: 'ME',
    country: 'Middle East',
    language: 'ar',
    currency: 'USD',
  },
  {
    domains: ['matwprojectmys.org'],
    region: 'MY',
    country: 'Malaysia',
    language: 'en',
    currency: 'MYR',
  },
  {
    domains: ['matwprojectsgp.org'],
    region: 'SG',
    country: 'Singapore',
    language: 'en',
    currency: 'SGD',
  },
  {
    domains: ['matwproject.org/ar'],
    region: 'AR',
    country: 'Arabic',
    language: 'ar',
    currency: 'AUD',
  },
  {
    domains: ['matwproject.fr.org', 'matwproject.org/fr'],
    region: 'FR',
    country: 'France',
    language: 'fr',
    currency: 'EUR',
  },
];

/**
 * Utility function to get region/country info from a domain or location string.
 * @param {string} [locationOrDomain] - URL, domain, or location string. If omitted, uses window.location.
 * @returns {object} Region info: { region, country, domain, language, currency, isDefault, isUAT }
 */
export function getDomainRegion(locationOrDomain) {
  let hostname = '';
  let pathname = '';
  if (locationOrDomain) {
    try {
      const parsed = new URL(locationOrDomain);
      hostname = parsed.hostname;
      pathname = parsed.pathname || '';
    } catch {
      // If just a hostname
      hostname = locationOrDomain;
      pathname = '';
    }
  } else if (typeof window !== 'undefined') {
    hostname = window.location.hostname;
    pathname = window.location.pathname || '';
  }
  if (hostname === 'localhost') hostname = 'localhost';
  if (hostname.startsWith('uat.')) {
    return {
      region: 'UAT',
      country: 'User Acceptance Testing',
      domain: hostname,
      language: 'en',
      currency: 'AUD',
      isUAT: true,
    };
  }
  for (const entry of DOMAIN_REGION_MAP) {
    for (const domain of entry.domains) {
      if (
        (domain.includes('/') && (hostname + pathname).startsWith(domain)) ||
        hostname === domain.replace(/^https?:\/\//, '')
      ) {
        return { ...entry, domain };
      }
    }
  }
  const fallback = DOMAIN_REGION_MAP.find(e => e.isDefault) || DOMAIN_REGION_MAP[0];
  return { ...fallback, domain: hostname };
}

/**
 * React hook to get region/country info for the current or given location.
 * - For SPA route changes, pass window.location.origin + location.pathname (or useLocation from react-router-dom).
 * @param {string} [url] Optional URL or domain to check. If omitted, uses window.location.
 * @returns {object} Region info: { region, country, domain, language, currency, isDefault, isUAT }
 */
export default function useDomainRegion(url) {
  return useMemo(() => getDomainRegion(url), [url]);
} 